import type { Ride } from '../domain/ride';
import { calculateRideMetrics } from '../domain/ride';
import { compileCameraPlan } from '../domain/camera-plan';
import { filmDuration } from '../domain/film-timing';
import { hasCameraKeys } from '../domain/camera-keyframes';
import type { CinematicSettings } from '../domain/cinematic-plan';
import { browserUuid } from '../domain/browser-crypto';
import { upsertSceneKey } from './scene-keyframes';
import { initializeStructuresView, structuresOverrides, STRUCTURES_TILT } from './structures-view';
import type { StudioDraft } from './studio-draft';

/** Give an untouched Structures view its camera while retaining authored compositions. */
export function initializeOpeningStructures(ride: Ride, draft: StudioDraft): StudioDraft {
  if (draft.freshEdit) return draft;
  if (draft.mapStyle !== 'relief' || draft.distance !== 'standard'
    || draft.cameraAngle !== null && draft.cameraAngle !== STRUCTURES_TILT
    || draft.cinematicSequence.waypoints.length || hasCameraKeys(draft.cinematicSequence.cameraTracks)
    || draft.cinematicSequence.cameraTracks?.targetLock === false) return draft;
  const points = ride.sourcePoints ?? ride.points;
  if (points.length < 2) return draft;
  const duration = filmDuration((ride.metrics ?? calculateRideMetrics(ride.points)).distanceKilometers, draft.timing);
  const pose = compileCameraPlan(points, draft.camera,
    { width: 1920, height: 1080, padding: { top: 0, bottom: 0, left: 0, right: 0 } }, duration, draft.distance).evaluate(0);
  const settings: CinematicSettings = { cameraStyle: draft.camera, cameraDistance: draft.distance, cameraAngle: draft.cameraAngle,
    cameraGimbal: draft.cameraGimbal, mapStyle: draft.mapStyle, depthOfField: draft.depthOfField };
  const sequence = upsertSceneKey(points, draft.cinematicSequence, 0, duration, browserUuid(), structuresOverrides()).sequence;
  return initializeStructuresView({ ...draft, cameraStyleVersion: 4, cameraAngle: STRUCTURES_TILT, cinematicEnabled: true,
    cinematicSequence: sequence }, pose, settings, 0, duration);
}
